import React, { useMemo, useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import {
  PlayCircle, Sparkles, ArrowRight, Star, Calendar, Briefcase, Heart, Coins,
  Flower2, Activity, Quote, MapPin, Trophy, ArrowDown,
} from "lucide-react";

import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import PaywallGate from "../components/PaywallGate";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useAuth } from "../context/AuthContext";
import { useToolUnlock } from "../lib/toolUnlock";
import api from "../lib/api";

const DIM_META = [
  { key: "career",    label: "Career",    icon: Briefcase, color: "bg-amber-500"   },
  { key: "money",     label: "Money",     icon: Coins,     color: "bg-emerald-500" },
  { key: "love",      label: "Love",      icon: Heart,     color: "bg-rose-500"    },
  { key: "spiritual", label: "Spiritual", icon: Flower2,  color: "bg-violet-500"  },
  { key: "emotional", label: "Emotional", icon: Activity,  color: "bg-sky-500"     },
];

const MOMENT_TYPE_LABEL = {
  transit: "TRANSIT",
  return:  "RETURN",
  ad:      "SUB-PERIOD",
};

const MOMENT_TYPE_TINT = {
  transit: "bg-violet-100 text-violet-900 border-violet-200",
  return:  "bg-amber-100 text-amber-900 border-amber-200",
  ad:      "bg-slate-100 text-slate-900 border-slate-200",
};

const STATUS_BADGE = {
  past:    { label: "PAST",   tint: "bg-slate-800/80 text-slate-100" },
  current: { label: "NOW",    tint: "bg-amber-400 text-amber-950" },
  future:  { label: "FUTURE", tint: "bg-indigo-200 text-indigo-900" },
};

// Tailwind needs to see these class strings literally at build time. Keep this map
// authoritative on the frontend so the page is robust even if the backend's
// `color_grad` field changes.
const PLANET_GRAD = {
  Sun:     "from-amber-500 via-orange-600 to-red-700",
  Moon:    "from-indigo-700 via-slate-700 to-slate-900",
  Mars:    "from-red-600 via-orange-700 to-rose-800",
  Mercury: "from-emerald-600 via-teal-700 to-cyan-800",
  Jupiter: "from-yellow-500 via-amber-600 to-orange-700",
  Venus:   "from-pink-500 via-rose-600 to-fuchsia-700",
  Saturn:  "from-slate-700 via-blue-800 to-indigo-900",
  Rahu:    "from-violet-600 via-purple-700 to-fuchsia-800",
  Ketu:    "from-gray-600 via-slate-700 to-zinc-900",
};

function statusKey(c) {
  if (c.is_current) return "current";
  if (c.is_past) return "past";
  return "future";
}

export default function LifeReplayPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata", name: "" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [submitted, setSubmitted] = useState(false);  // birth form completed & sent
  const resultRef = useRef(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);
  const { user } = useAuth();

  // Server-side paywall now guards `/life-replay/compute` (returns 402
  // until this user has unlocked "life_replay" for this fingerprint). We
  // mirror that here so we NEVER hit the endpoint pre-payment — saves
  // Universal-Key credits AND avoids showing a spinner-then-error dance.
  const birthDetails = { date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone };
  const hasBirth = !!(form.date && form.time && (form.place || placeQuery));
  const { unlocked } = useToolUnlock({
    tool: "life_replay",
    birthDetails: hasBirth ? birthDetails : null,
    user,
  });

  const runCompute = async () => {
    if (loading || data) return;
    setLoading(true);
    try {
      const r = await api.post("/life-replay/compute", { ...form, place: form.place || placeQuery });
      setData(r.data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 120);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to replay your life");
    } finally {
      setLoading(false);
    }
  };

  // Fire compute ONLY once the user is unlocked (either they paid, or they
  // were already unlocked from a prior visit) AND they've submitted birth
  // details. This is the crux of the paywall-first UX — no LLM call ever
  // happens without a paid unlock in place.
  useEffect(() => {
    if (submitted && unlocked && !data && !loading) runCompute();
  }, [submitted, unlocked]);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setSubmitted(true);
    // If already unlocked (returning paid user), effect above will fire
    // compute. Otherwise the standalone paywall CTA appears.
  };

  const chapters = data?.chapters || [];
  const currentIndex = useMemo(() => chapters.findIndex((c) => c.is_current), [chapters]);

  return (
    <PageShell>
      <SEO
        title="Life Replay — Your Karmic Timeline Visualized | Cinematic Vedic Life Timeline"
        description="A cinematic, scrollable life timeline powered by Vimshottari Dasha + Saturn/Jupiter transits. See your past chapters decoded and your future eras previewed."
        path="/life-replay"
        keywords={[
          "life timeline astrology", "cinematic life timeline", "dasha timeline",
          "vimshottari visual", "karmic timeline", "future predictions astrology",
          "saturn return", "jupiter return",
        ]}
      />

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0F0A1A] via-[#1A0F2A] to-[#0A0612] text-white relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(251,191,36,.18), transparent 40%), radial-gradient(circle at 80% 70%, rgba(139,92,246,.18), transparent 40%)" }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-14 pb-20 relative">
          <div className="label-eyebrow text-amber-300 flex items-center gap-2">
            <PlayCircle size={14} /> Life Replay · Your Karmic Timeline
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl mt-3 font-light leading-tight text-white">
            <span className="text-amber-300">Replay</span> your life —<br className="hidden sm:block" />
            past, present <span className="opacity-60">&</span> future
          </h1>
          <p className="text-white/80 text-base sm:text-lg mt-5 font-light max-w-3xl leading-relaxed">
            The first <b className="text-amber-200">cinematic, chapter-by-chapter</b> life timeline powered by Vimshottari
            Dasha. We walk your 9 karmic eras from birth to age 90, overlay every{" "}
            <b className="text-amber-200">Saturn / Jupiter transit</b> over your natal points, mark your{" "}
            <b className="text-amber-200">Saturn returns</b> and <b className="text-amber-200">Jupiter returns</b>,
            and hand you a biopic for every chapter of your life.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-xs text-white/70">
            <Pill icon={Calendar} text="9 chapters · 90 years" />
            <Pill icon={Star} text="Transit + Dasha overlay" />
            <Pill icon={Sparkles} text="Personalised" />
          </div>
        </div>
      </div>

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10">
        <div className="label-eyebrow text-muted mb-3">Enter your birth details to begin the replay</div>
        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-3xl" data-testid="life-replay-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                     className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                     data-testid="life-replay-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                     className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                     data-testid="life-replay-time" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Your first name <span className="text-muted/60">(optional)</span></span>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Aanya"
                     className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                     data-testid="life-replay-name" />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="life-replay-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`life-replay-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading}
                  className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50"
                  data-testid="life-replay-submit">
            {loading ? "Replaying your life…" : (<>Start my replay <ArrowRight size={16} className="inline ml-1" /></>)}
          </button>
        </form>
      </div>

      {/* ── Standalone paywall — submitted but not yet unlocked ─────────────── */}
      {submitted && !unlocked && (
        <div ref={resultRef} className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-16" data-testid="life-replay-paywall-only">
          <PaywallGate
            tool="life_replay"
            toolLabel="Life Replay · Your Karmic Timeline"
            birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
            variant="standalone"
          />
        </div>
      )}

      {/* ── Loading state while compute + LLM narrative runs ─────────────────── */}
      {submitted && unlocked && loading && !data && (
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-16 text-center" data-testid="life-replay-loading">
          <div className="animate-pulse text-muted text-sm">Casting your 90-year timeline — this takes ~20 seconds…</div>
        </div>
      )}

      {/* ── Results (only rendered once compute finishes, i.e. user is unlocked) ── */}
      {data && (
        <div ref={resultRef} className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-14 pb-24" data-testid="life-replay-result">
          {/* ── Cinematic intro slide ── */}
          <section className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#0F0A1A] via-[#1A0F2A] to-[#0A0612] text-white p-8 sm:p-12 shadow-2xl relative">
            <div aria-hidden className="absolute inset-0 opacity-20"
                 style={{ backgroundImage: "radial-gradient(circle at 30% 40%, rgba(251,191,36,.4), transparent 50%), radial-gradient(circle at 70% 60%, rgba(236,72,153,.3), transparent 50%)" }} />
            <div className="relative">
              <div className="label-eyebrow text-amber-300">Your Life · The Replay</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
                {data.birth.lagna} lagna · <span className="text-amber-300">{data.birth.moon_rashi} moon</span>
              </h2>
              <p className="text-white/80 mt-4 text-sm sm:text-base flex items-center gap-2 flex-wrap">
                <MapPin size={14} className="text-amber-300" /> {data.birth.place}
                <span className="text-white/50">·</span>
                <span>Age <b className="text-amber-300">{data.current_age}</b> · {chapters.length} chapters</span>
              </p>

              {/* Lifetime dimension averages — Apple Health style */}
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
                {DIM_META.map((d) => (
                  <DimChip key={d.key} dim={d} value={data.lifetime_dimensions?.[d.key] || 0} dark />
                ))}
              </div>

              {currentIndex >= 0 && (
                <div className="mt-8 inline-flex items-center gap-2 text-amber-200 text-sm">
                  <ArrowDown size={14} className="animate-bounce" />
                  Scroll for the full replay — you&apos;re inside Chapter {currentIndex + 1}
                </div>
              )}
            </div>
          </section>

          {/* ── Chapters timeline — full unlock, no free-preview split ── */}
          <div className="mt-10 space-y-6" data-testid="life-replay-full-timeline">
            {chapters.map((c, idx) => (
              <ChapterCard key={c.id} chapter={c} index={idx + 1} totalChapters={chapters.length} />
            ))}

            {/* Closing card — final reflection */}
            <section className="rounded-3xl overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-8 sm:p-12 text-center">
              <Trophy size={32} className="text-amber-700 mx-auto" />
              <h3 className="font-serif-display text-3xl sm:text-4xl text-amber-900 mt-4 leading-tight">
                That&apos;s your replay.
              </h3>
              <p className="text-amber-900/80 text-sm sm:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
                Past chapters are decoded. Current chapter is live. Future chapters are previews.
                Bookmark this page — it updates the same way as your dasha turns.
              </p>
            </section>
          </div>
        </div>
      )}
    </PageShell>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function Pill({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-3 py-1 rounded-full">
      <Icon size={12} /> {text}
    </span>
  );
}

function DimChip({ dim, value, dark }) {
  const Icon = dim.icon;
  const pct = Math.max(0, Math.min(100, (value / 10) * 100));
  return (
    <div className={`rounded-xl p-3 border ${dark ? "bg-white/5 border-white/15 text-white" : "bg-white border-soft text-[#3F2D1B]"}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[10px] tracking-widest font-semibold opacity-80">
          <Icon size={11} /> {dim.label.toUpperCase()}
        </span>
        <span className="text-xs font-bold">{value}</span>
      </div>
      <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${dark ? "bg-white/10" : "bg-soft"}`}>
        <div className={`h-full ${dim.color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ChapterCard({ chapter, index, totalChapters }) {
  const status = statusKey(chapter);
  const sb = STATUS_BADGE[status];
  return (
    <article
      className={`rounded-3xl overflow-hidden shadow-xl border ${
        status === "current" ? "border-amber-300 shadow-amber-500/20 ring-2 ring-amber-300/30" :
        status === "future" ? "border-indigo-200" : "border-slate-200"
      }`}
      data-testid={`life-chapter-${chapter.id}`}
    >
      {/* Big gradient banner */}
      <div className={`relative px-6 sm:px-10 py-8 sm:py-10 bg-gradient-to-br ${PLANET_GRAD[chapter.md_lord] || "from-slate-700 to-slate-900"} text-white overflow-hidden`}>
        <div aria-hidden className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,.5), transparent 40%)" }} />
        <div className="relative">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="label-eyebrow !text-white/80">
              Chapter {index} of {totalChapters} · {chapter.md_lord}
            </div>
            <span className={`text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full ${sb.tint}`}>{sb.label}</span>
          </div>
          <div className="mt-2 flex items-baseline gap-4 flex-wrap">
            <div className="font-serif-display text-3xl sm:text-4xl">
              age {chapter.age_range[0]}<span className="opacity-50"> — </span>{chapter.age_range[1]}
            </div>
            <div className="text-white/80 text-sm">
              {chapter.start_date.slice(0, 4)} – {chapter.end_date.slice(0, 4)}
              <span className="opacity-50"> · </span>{chapter.duration_years} years
            </div>
          </div>
          <h3 className="font-serif-display text-2xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
            {chapter.headline}
          </h3>
          {chapter.narrative && (
            <p className="text-white/90 mt-4 text-base sm:text-lg leading-relaxed max-w-3xl">
              {chapter.narrative}
            </p>
          )}
          {chapter.key_insight && (
            <blockquote className="mt-6 flex items-start gap-3 max-w-2xl">
              <Quote size={20} className="opacity-60 mt-1 flex-shrink-0" />
              <p className="font-serif-display italic text-lg sm:text-xl text-white/95 leading-snug">
                {chapter.key_insight}
              </p>
            </blockquote>
          )}
        </div>
      </div>

      {/* Body — dimensions + moments */}
      <div className="bg-[#FAF4E8] px-6 sm:px-10 py-8">
        <div className="label-eyebrow text-muted mb-3">5-domain intensity for this era</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {DIM_META.map((d) => (
            <DimChip key={d.key} dim={d} value={chapter.dimensions[d.key] || 0} />
          ))}
        </div>

        {chapter.moments?.length > 0 && (
          <>
            <div className="label-eyebrow text-muted mt-8 mb-3">Key moments inside this chapter</div>
            <ol className="relative border-l-2 border-amber-300 ml-3 space-y-4">
              {chapter.moments.map((m, i) => (
                <li key={i} className="pl-6 relative">
                  <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-[#FAF4E8]" />
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs text-muted font-mono">{m.date}</span>
                    <span className={`text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded border ${MOMENT_TYPE_TINT[m.type] || ""}`}>
                      {MOMENT_TYPE_LABEL[m.type] || m.type.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-[#3F2D1B] mt-0.5 leading-snug">{m.label}</p>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </article>
  );
}
